import React, { useEffect, useMemo, useState } from 'react';
import InputShortText from '../InputShortText/InputShortText';
import TextArea from '../TextArea/TextArea';
import { useTranslation } from 'react-i18next';
import { isValidEmail, isValidPhone, isValidText } from '../../shared/validation';
import { useFormValidation } from '../../hooks/useFormValidation';
import SelectInput from '../SelectInput/SelectInput';
import CONFIG from '../../config/config';
import { Customer } from '../../api/CustomersAPI';
import { Employee } from '../../api/EmployeesAPI';
import { getCustomerStatuses } from '../../shared/customerStatusToTranslationKey';
import TinyText from '../TinyText/TinyText';
import CustomDatePicker from '../CustomDatePicker/CustomDatePicker';
import SectionTitle from '../SectionTitle/SectionTitle';
import { getInboundChannels, inboundChannelToTranslationKeys } from '../../shared/inboundChannelToTranslationKey';
import PhoneNumberInput from '../PhoneNumberInput/PhoneNumberInput';
import { paymentStatusToAbbreviatedTranslationKey, paymentStatusToPillColor, paymentStatusToTranslationKey, bookingPaymentStatusToTranslationKey, bookingPaymentStatusToPillColor } from '../../shared/paymentStatusToTranslationKey';
import Tooltip from '../Tooltip/Tooltip';
import Pill from '../Pill/Pill';
import { BolderText } from '../BolderText/BolderText';
import { orderStatusToTranslationKeys } from '../../shared/orderStatusToTranslationKey';
import { saleStatusToPillColor, saleStatusToTranslationKey } from '../../shared/saleStatusToTranslationKey';
import { Order, OrderDress } from '../../api/OrdersAPI';
import { meetingTypeToTranslationKey } from '../../shared/meetingTypeToTranslationKey';
import { parseDateTime, safeParseDate, createDateComparator, parseLegacyDateArray } from '../../shared/timeUtils';
import Table, { ColumnDefinition } from '../Table/Table';
import { OrderColumn } from '../OrdersSegment/OrdersSegment';
import { Meeting } from '../../api/MeetingsAPI';
import { Sale } from '../../api/SalesAPI';
import SaleDiscountIndicator from '../SaleDiscountIndicator/SaleDiscountIndicator';
import { UserX, UserCheck, Bell, BellOff, ExternalLink } from 'lucide-react';
import { getMeetingBorderColor, getMeetingColor } from '../../shared/meetingTypeToColor';
import { CustomerMeetingEnriched } from '../../api/CustomersAPI';
import CopyText from '../CopyText/CopyText';
import SmallCheckBox from '../SmallCheckBox/SmallCheckBox';
import { loadSalesRepresentativesForSelect } from '../../utils/loadSalesRepresentativesForSelect';

type Props = {
  customer: Customer,
  onCustomerChange?: (value: Customer) => void,
  isDisabled?: boolean,
  actionButton?: JSX.Element
};

export type MeetingColumn = ColumnDefinition<Meeting>;
export type SaleColumn = ColumnDefinition<Sale>;

const getInitialState = (customer?: Customer) => {
  if (customer) {
    return {
      name: isValidText(customer.name, 3),
      surname: isValidText(customer.surname, 3),
      email: isValidEmail(customer.email),
      phone: isValidPhone(customer.phone)
    };
  }
};

function CustomerForm({ customer, onCustomerChange = () => null, isDisabled = false, actionButton }: Props) {
  
  const { t } = useTranslation();
  const requiredFields = ['name'];
  const initialState = getInitialState(customer);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const { updateFieldValidation, isFormValid } = useFormValidation(requiredFields, initialState);
  const showOrdersSection = false; // intentionally hidden for now

  const renderOrderDressName = (dress?: OrderDress, dressDescription?: string): React.ReactNode => {
    return (
      <Tooltip content={dress?.name || dressDescription || '-'}>
        <p className="w-32 truncate">{dress?.name || dressDescription}</p>
      </Tooltip>
    );
  };

  const orderColumns: OrderColumn[] = [
    {
      key: 'shopName',
      title: t("orders.details.salon"),
      render: (order) => 
      <Tooltip content={order.shopName!}>
        <p className="w-36 truncate">{order.shopName}</p>
      </Tooltip>,
      rowClass: (or) => 
      `
        ${or.visible && or.status !== 'BLOCKED' ? 'hover:bg-darker-gold-50' : ''} 
        ${or.status === 'BLOCKED' ? 'bg-custom-red' : ''}
        ${!or.visible ? 'bg-darker-gold-100 text-grey-50' : ''} 
        ${or.status === 'SENT' && or.visible ? 'bg-darker-gold-300' : ''}
      `
    },
    {
      key: 'paymentStatus',
      title: t("orders.details.payment_status"),
      render: (order) => (
        <Tooltip content={t(paymentStatusToTranslationKey(order.paymentStatus))}>
          <Pill text={t(paymentStatusToAbbreviatedTranslationKey(order.paymentStatus))} color={paymentStatusToPillColor(order.paymentStatus)}/>
        </Tooltip>
      ),
    },
    {
      key: 'dress',
      title: t("orders.details.description"),
      render: (order) => renderOrderDressName(order.dress, order.dressDescription),
    },
    {
      key: 'deliveryDate',
      title: t("orders.details.delivery_time"),
      render: (order) => <BolderText text={order.deliveryDate} /> ,
    },
    {
      key: 'status',
      title: t("orders.details.status"),
      render: (order) => <Tooltip content={t(orderStatusToTranslationKeys(order.status))}><p className="w-12 sm:w-20 truncate">{ t(orderStatusToTranslationKeys(order.status)) }</p></Tooltip>,
    },
  ];

  const meetingColumns: MeetingColumn[] = [
    {
      key: 'type',
      title: t("calendar.meeting.form.type"),
      render: (meeting) => {
        const meetingTypeColor = getMeetingColor(meeting.type);
        const meetingBorderColor = getMeetingBorderColor(meeting.type);
        return (
          <Pill 
            text={meeting.bookedMeetingType || t(meetingTypeToTranslationKey(meeting.type))} 
            color="grey"
            customColor={{
              backgroundColor: meetingTypeColor,
              borderColor: meetingBorderColor,
              textColor: '#000'
            }}
          />
        );
      },
    },
    {
      key: 'startTime',
      title: t("calendar.meeting.form.start"),
      render: (meeting) => (
        <div className="flex flex-col space-y-1">
          <BolderText text={parseDateTime(meeting.startTime).date} />
          <span className="text-sm text-gray-600">
            {parseDateTime(meeting.startTime).time} - {parseDateTime(meeting.endTime).time}
          </span>
        </div>
      ),
    },
    {
      key: 'paymentStatus' as keyof Meeting,
      title: t("customers.meetings.payment_status") || "Payment Status",
      render: (meeting) => {
        const { enriched } = getEnrichedMeeting(meeting);
        const status = enriched?.paymentInfo?.paymentStatus;
        if (!status || status === 'NOT_REQUIRED') {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <Tooltip content={t(bookingPaymentStatusToTranslationKey(status))}>
            <Pill 
              text={t(bookingPaymentStatusToTranslationKey(status))} 
              color={bookingPaymentStatusToPillColor(status)}
            />
          </Tooltip>
        );
      },
    },
    {
      key: 'bookingLink' as keyof Meeting,
      title: t("customers.meetings.booking_link") || "Booking",
      render: (meeting) => {
        const { enriched } = getEnrichedMeeting(meeting);
        if (!enriched?.bookingTrackingUrl) {
          return <span className="text-gray-400">-</span>;
        }
        const fullUrl = `${window.location.origin}${enriched.bookingTrackingUrl}`;
        
        return (
          <div className="flex items-center space-x-2">
            <CopyText text={fullUrl} />
            <a 
              href={enriched.bookingTrackingUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 transition-colors"
              title={t("customers.meetings.booking_link") || "View booking"}
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        );
      },
    },
    {
      key: 'reminder',
      title: t("calendar.meeting.form.phone.reminder"),
      render: (meeting) => (
        <span className={meeting.reminder ? "text-amber-500" : "text-gray-400"}>
          {meeting.reminder ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
        </span>
      ),
    },
    {
      key: 'noShow',
      title: t("calendar.meeting.form.noshow"),
      render: (meeting) => (
        <span className={meeting.noShow ? "text-red-600" : "text-green-600"}>
          {meeting.noShow ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
        </span>
      ),
    },
  ];

  const cancelledMeetingColumns: MeetingColumn[] = [
    {
      key: 'type',
      title: t("calendar.meeting.form.type"),
      render: (meeting) => {
        const meetingTypeColor = getMeetingColor(meeting.type);
        const meetingBorderColor = getMeetingBorderColor(meeting.type);
        return (
          <Pill 
            text={meeting.bookedMeetingType || t(meetingTypeToTranslationKey(meeting.type))} 
            color="grey"
            customColor={{
              backgroundColor: meetingTypeColor,
              borderColor: meetingBorderColor,
              textColor: '#000'
            }}
          />
        );
      },
    },
    {
      key: 'startTime',
      title: t("calendar.meeting.form.start"),
      render: (meeting) => (
        <div className="flex flex-col space-y-1 opacity-70">
          <BolderText text={parseDateTime(meeting.startTime).date} />
          <span className="text-sm text-gray-600">
            {parseDateTime(meeting.startTime).time} - {parseDateTime(meeting.endTime).time}
          </span>
        </div>
      ),
    },
    {
      key: 'removalReason' as keyof Meeting,
      title: t("calendar.removed_meetings.removal_reason"),
      render: (meeting) => (
        meeting.removalReason ? (
          <div className="flex flex-col space-y-1">
            <Pill text={t(`stats.removal_reason.${meeting.removalReason}`)} color="grey" />
            {meeting.removalReasonNote ? (
              <span className="text-xs text-muted-foreground max-w-[12rem] truncate" title={meeting.removalReasonNote}>
                {meeting.removalReasonNote}
              </span>
            ) : null}
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        )
      ),
    },
  ];

  const saleColumns: SaleColumn[] = [
    {
      key: 'saleDate',
      title: t("sales.details.date") || "Sale Date",
      render: (sale) => <BolderText text={sale.saleDate || ''} />,
    },
    {
      key: 'receiptNumber',
      title: t("sales.details.receipt_number") || "Receipt Number",
      render: (sale) => (
        <Tooltip content={sale.receiptNumber || '-'}>
          <p className="w-32 truncate font-medium">{sale.receiptNumber || '-'}</p>
        </Tooltip>
      ),
    },
    {
      key: 'dressName',
      title: t("sales.details.dress") || "Dress",
      render: (sale) => (
        sale.dressDescription ? (
          <Tooltip content={sale.dressDescription}>
            <p className="w-32 truncate">{sale.dressName}</p>
          </Tooltip>
        ) : (
          <p className="w-32 truncate">{sale.dressName}</p>
        )
      ),
    },
    {
      key: 'totalPrice',
      title: t("sales.details.price") || "Price",
      render: (sale) => (
        <span className="inline-flex items-center gap-1 font-medium">
          {sale.totalPrice?.toLocaleString()} {sale.currency}
          <SaleDiscountIndicator sale={sale} />
        </span>
      ),
    },
    {
      key: 'paymentStatus',
      title: t("sales.details.payment_status") || "Payment Status",
      render: (sale) => (
        <Tooltip content={t(paymentStatusToTranslationKey(sale.paymentStatus as any))}>
          <Pill 
            text={t(paymentStatusToAbbreviatedTranslationKey(sale.paymentStatus as any))} 
            color={paymentStatusToPillColor(sale.paymentStatus as any)}
          />
        </Tooltip>
      ),
    },
    {
      key: 'status',
      title: t("sales.details.status") || "Status",
      render: (sale) => {
        if (!sale.status) {
          return <span className="text-gray-400">-</span>;
        }

        const translationKey = saleStatusToTranslationKey(sale.status);
        const label = translationKey === "global.unknown" ? sale.status : t(translationKey);

        return (
          <Pill
            text={label}
            color={saleStatusToPillColor(sale.status)}
          />
        );
      },
    },
    {
      key: 'id',
      title: '',
      render: (sale) => {
        if (!sale?.id) return null;
        return (
          <div className="flex justify-end">
            <Tooltip content={t("global.open") || "Open"}>
              <a
                href={`/sales?saleId=${sale.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 transition-colors"
                aria-label={t("global.open") || "Open"}
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    const load = async () => {
      try {
        const list = await loadSalesRepresentativesForSelect(customer?.employeeId);
        setEmployees(list);
      } catch (error) {
        console.error('Failed to fetch employees:', error);
      }
    };
    load();
  }, [customer?.employeeId]);

  const disabledActionButton = actionButton
    ? React.cloneElement(
        actionButton,
        {},
        React.Children.map(actionButton.props.children, (child) => {
          return child ? React.cloneElement(child, { isDisabled: !isFormValid() }) : null;
        })
      )
    : null;

  const getEnrichedMeeting = (meeting: Meeting): { meeting: Meeting; enriched?: CustomerMeetingEnriched } => {
    const enriched = customer?.enrichedMeetings?.find(em => em.meeting?.id === meeting.id);
    return { meeting, enriched };
  };

  const { upcomingMeetings, pastMeetings, cancelledMeetings } = useMemo(() => {
    const result = {
      upcomingMeetings: [] as Meeting[],
      pastMeetings: [] as Meeting[],
      cancelledMeetings: [] as Meeting[],
    };
    const all = (customer?.enrichedMeetings?.map(em => em.meeting) || customer?.meetings || []);
    if (all.length === 0) return result;
    const nowTs = Date.now();
    const upcoming: Meeting[] = [];
    const past: Meeting[] = [];
    const cancelled: Meeting[] = [];
    for (const m of all) {
      if (!m || !m.endTime) continue;
      if (m.disable) {
        cancelled.push(m);
        continue;
      }
      const endTs = new Date(m.endTime).getTime();
      if (!isNaN(endTs) && endTs >= nowTs) {
        upcoming.push(m);
      } else if (!isNaN(endTs)) {
        past.push(m);
      }
    }
    const sortByStartTime = createDateComparator<Meeting>((m) => m.startTime);
    upcoming.sort(sortByStartTime);
    past.sort(createDateComparator<Meeting>((m) => m.startTime, false));
    cancelled.sort(createDateComparator<Meeting>((m) => m.lastUpdateTime || m.startTime, false));
    return { upcomingMeetings: upcoming, pastMeetings: past, cancelledMeetings: cancelled };
  }, [customer?.meetings, customer?.enrichedMeetings]);

  return (
    <>
      <div className="flex flex-wrap space-x-5">
        <div className="md:flex-1 w-full space-y-6">
          <div className="flex flex-wrap space-x-5">
            <InputShortText 
              label={`${t("customers.form.name")}*`} 
              validationFn={(value) => !!value && isValidText(value, 3)}
              onValidationChange={(isValid) => updateFieldValidation('name', isValid)}
              onChange={e => onCustomerChange({ ...customer, name: e.currentTarget.value } as Customer)}
              errorMessage={t("errors.too_short")!}
              defaultValue={customer?.name || ''}
              isDisabled={isDisabled}
            />
            <InputShortText 
              label={`${t("customers.form.surname")}`} 
              validationFn={(value) => !!value && isValidText(value, 3)}
              onValidationChange={(isValid) => updateFieldValidation('surname', isValid)}
              onChange={e => onCustomerChange({ ...customer, surname: e.currentTarget.value } as Customer)}
              errorMessage={t("errors.too_short")!}
              defaultValue={customer?.surname || ''}
              isDisabled={isDisabled}
            />
          </div>
          <div className="flex flex-wrap space-x-5">
            <SelectInput
                values={getCustomerStatuses().map(e => { return { value: e, readableValue: t(`customers.status.${e.toLocaleLowerCase()}`) } })}
                onChange={e => onCustomerChange({ ...customer, status: e.currentTarget.value } as Customer)}
                label={"customers.form.status"}
                defaultValue={customer?.status}
                isDisabled={isDisabled}
            />
            <SelectInput
              values={employees.map(e => { return { value: String(e.id!), readableValue: e.name }})}
              onChange={e => onCustomerChange({ ...customer, employeeId: e.target.value } as Customer)}
              label={"customers.form.employee"}
              defaultValue={customer?.employeeId}
              isDisabled={isDisabled}
            />
          </div>
          <SmallCheckBox
            defaultValue={customer?.flagged || false}
            isDisabled={isDisabled}
            label="customers.form.flagged"
            onChange={e => onCustomerChange({ ...customer, flagged: e.target.checked } as Customer)}
            helperText="customers.form.flagged.helper"
          />

          <SectionTitle text={t("customers.form.contact")} />
          <div className="flex flex-wrap space-x-5">
            <InputShortText 
              label={`${t("customers.form.email")}`} 
              validationFn={(value) => !!value && isValidEmail(value)}
              onValidationChange={(isValid) => updateFieldValidation('email', isValid)}
              onChange={e => onCustomerChange({ ...customer, email: e.currentTarget.value } as Customer)}
              errorMessage={t("errors.invalid_email")!}
              defaultValue={customer?.email || ''}
              isDisabled={isDisabled}
            />
          </div>
          <div className="flex flex-wrap">
            <PhoneNumberInput 
              label={"calendar.meeting.form.phone"}
              validationFn={(value) => !value || isValidPhone(value)} 
              onChange={e => onCustomerChange({ ...customer, phone: e } as Customer)}
              defaultValue={customer?.phone || ''}
              isDisabled={isDisabled}
            />
          </div>

          <SectionTitle text={t("customers.form.demographics")} />
          <div className="flex flex-col md:flex-row md:flex-wrap space-y-5 md:space-y-0 md:space-x-5">
            <CustomDatePicker
              value={Array.isArray(customer?.weddingDate) 
                ? parseLegacyDateArray(customer.weddingDate)
                : safeParseDate(customer?.weddingDate)}
              onChange={e => onCustomerChange({ ...customer, weddingDate: e.toISOString() } as Customer)}
              label={"customers.form.wedding_date"}
              dateFormat="LL/yyyy"
              isDisabled={isDisabled}
              disablePast={false}
            />
            <SelectInput
              values={CONFIG.COUNTRY_CODES.map(e => { return { value: e.countryCode, readableValue: e.countryName } })}
              onChange={e => onCustomerChange({ ...customer, countryCode: e.currentTarget.value } as Customer)}
              label={`${t("customers.form.country")}`} 
              defaultValue={customer?.countryCode}
              isDisabled={isDisabled}
            />
            <SelectInput
              values={getInboundChannels().map(e => { return { value: e, readableValue: t(inboundChannelToTranslationKeys(e))} })}
              onChange={e => onCustomerChange({ ...customer, inboundChannel: e.currentTarget.value } as Customer)}
              label={"customers.form.inbound_channel"}
              defaultValue={customer?.inboundChannel}
              isDisabled={isDisabled}
            />
          </div>
          {showOrdersSection && (
            <>
              <SectionTitle text={t("customers.form.orders")} />
              <Table<Order>
                  data={customer?.orders || []}
                  columns={orderColumns}
                  compareFunction={(item, selectedItem) => item.id === selectedItem.id}
                  emptyListComponent={<TinyText text={`${t("customers.orders.empty")}.` }/>}
                  selectedItems={[]}
                />
            </>
          )}
          <SectionTitle text={t("customers.form.sales") || "Sales"} />
          <Table<Sale>
              data={customer?.sales || []}
              columns={saleColumns}
              compareFunction={(item, selectedItem) => item.id === selectedItem.id}
              emptyListComponent={<TinyText text={`${t("customers.sales.empty") || "No sales found"}.` }/>}
              selectedItems={[]}
            />
          <SectionTitle text={t("customers.form.meetings")} />
          <TinyText text={t("customers.meetings.upcoming") || "Upcoming meetings"} />
          <Table<Meeting>
              data={upcomingMeetings}
              columns={meetingColumns}
              compareFunction={(item, selectedItem) => item.id === selectedItem.id}
              emptyListComponent={<><br/><TinyText text={`${t("customers.meetings.empty")}.` }/></>}
              selectedItems={[]}
            />
          <div className="mt-2">
            <TinyText text={t("customers.meetings.past") || "Past meetings"} />
            <Table<Meeting>
                data={pastMeetings}
                columns={meetingColumns}
                compareFunction={(item, selectedItem) => item.id === selectedItem.id}
                emptyListComponent={<><br/><TinyText text={`${t("customers.meetings.empty")}.` }/></>}
                selectedItems={[]}
              />
          </div>
          <div className="mt-2 opacity-90">
            <TinyText text={t("customers.meetings.cancelled") || "Cancelled meetings"} />
            <Table<Meeting>
                data={cancelledMeetings}
                columns={cancelledMeetingColumns}
                compareFunction={(item, selectedItem) => item.id === selectedItem.id}
                emptyListComponent={<><br/><TinyText text={`${t("customers.meetings.cancelled_empty")}.` }/></>}
                selectedItems={[]}
              />
          </div>
          <SectionTitle text={t("customers.form.notes")}/>
          <TextArea
            label={t("customers.form.notes")!} 
            validationFn={(value) => !!value && isValidText(value, 0, 500)}
            onChange={e => onCustomerChange({ ...customer, notes: e.currentTarget.value } as Customer)}
            defaultValue={customer?.notes || ''}
            isDisabled={isDisabled}
          />
        </div>
      </div>
      { disabledActionButton }
    </>
  );
}

export default CustomerForm;
